import { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight, Heart, Activity, Brain, Sun, Moon, Bone, Droplets, Wind, /* Sparkles */ } from 'lucide-react';
import type { Producto } from '@/types';
import productosData from '@/data/productos.json';

interface TratamientoData {
  icono: React.ElementType;
  color: string;
  descripcion: string;
}

const tratamientosInfo: Record<string, TratamientoData> = {
  'Diabetes': { icono: Activity, color: '#8abd31', descripcion: 'Control natural de glucosa' },
  'Hipertensión': { icono: Heart, color: '#e91e8c', descripcion: 'Regulación cardiovascular' },
  'Sobrepeso': { icono: Sun, color: '#f59e0b', descripcion: 'Control de peso saludable' },
  'Digestión': { icono: Wind, color: '#8abd31', descripcion: 'Sistema digestivo óptimo' },
  'Hígado Graso e Intoxicado': { icono: Droplets, color: '#8b5cf6', descripcion: 'Desintoxicación hepática' },
  'Colesterol': { icono: Activity, color: '#06b6d4', descripcion: 'Regulación lipídica' },
  'Artrosis': { icono: Bone, color: '#f97316', descripcion: 'Salud articular' },
  'Estrés': { icono: Brain, color: '#6366f1', descripcion: 'Bienestar mental' },
  'Insomnio': { icono: Moon, color: '#4f46e5', descripcion: 'Descanso reparador' },
  'default': { icono: Heart, color: '#8abd31', descripcion: 'Bienestar integral' }
};

type Page = 'inicio' | 'embajador' | 'catalogo' | 'tratamientos';

interface TratamientosProps {
  onNavigate: (page: Page, params?: { linea?: string; producto?: Producto }) => void;
  initialTratamiento?: string | null;
}

// Hook para detectar el ancho de pantalla
function useWindowWidth() {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return width;
}

interface ProductosDisplayProps {
  productos: Producto[];
  onProductClick: (producto: Producto) => void;
  windowWidth: number;
}

// Componente que muestra productos en GRID (desktop) o CARRUSEL (móvil/tablet)
function ProductosDisplay({ productos, onProductClick, windowWidth }: ProductosDisplayProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Desktop: mostrar todos en grid (sin carrusel)
  const isDesktop = windowWidth >= 1024;
  // Móvil: carrusel con 1 producto visible
  const isMobile = windowWidth < 640;

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    if (!isDesktop) {
      checkScroll();
    }
  }, [isDesktop, productos]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = isMobile ? 280 : 320;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (productos.length === 0) return null;

  // Tarjeta de producto reutilizable
  const ProductCard = ({ producto }: { producto: Producto }) => (
    <button
      onClick={() => onProductClick(producto)}
      className="text-left w-full"
    >
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 group/card h-full border border-transparent hover:border-[#e91e8c]/20">
        {/* Image */}
        <div className="aspect-[4/3] bg-gradient-to-br from-[#f8f9f5] to-[#ffe4f0]/30 flex items-center justify-center relative overflow-hidden">
          {producto.imagen && producto.imagen.length > 0 ? (
            <img 
              src={producto.imagen[0]} 
              alt={producto.nombre} 
              className="w-full h-full object-contain transition-transform duration-500 group-hover/card:scale-110" 
            />
          ) : (
            <span className="text-5xl transition-transform duration-500 group-hover/card:scale-110">
              🌿
            </span>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#e91e8c]/10 to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity" />
        </div>

        {/* Content */}
        <div className="p-4 md:p-5">
          <div className="text-xs text-[#e91e8c] font-medium mb-1">
            {producto.linea}
          </div>
          <h4 className="font-serif text-base md:text-lg text-[#8abd31] mb-2 line-clamp-1 group-hover/card:text-[#e91e8c] transition-colors">
            {producto.nombre}
          </h4>
          <p className="text-xs md:text-sm text-gray-500 line-clamp-2 mb-3">
            {producto.descripcion}
          </p>
          <div className="flex items-center justify-between">
            <span className="font-serif text-base md:text-lg font-bold text-[#e91e8c]">
              {producto.precio.split('/')[0]}
            </span>
            <span className="flex items-center gap-1 text-xs md:text-sm text-[#e91e8c] opacity-0 group-hover/card:opacity-100 transition-opacity">
              Ver más <ArrowRight className="w-3 h-3 md:w-4 md:h-4" />
            </span>
          </div>
        </div>
      </div>
    </button>
  );

  // Desktop: Grid con todos los productos
  if (isDesktop) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 md:gap-6">
        {productos.map((producto) => (
          <ProductCard key={producto.codigo} producto={producto} />
        ))}
      </div>
    );
  }

  // Tablet/Móvil: Carrusel
  return (
    <div className="relative group">
      {/* Navigation Arrows - Solo en tablet */}
      {!isMobile && canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 bg-white shadow-lg rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        >
          <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 text-[#e91e8c]" />
        </button>
      )}
      {!isMobile && canScrollRight && (
        <button
          onClick={() => scroll('right')}
          className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 bg-white shadow-lg rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        >
          <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-[#e91e8c]" />
        </button>
      )}

      {/* Products Scroll Container */}
      <div
        ref={scrollRef}
        className="flex gap-3 md:gap-4 overflow-x-auto scrollbar-hide pb-4 snap-x snap-mandatory"
        onScroll={checkScroll}
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {productos.map((producto) => (
          <div 
            key={producto.codigo}
            className={`flex-shrink-0 snap-start ${
              isMobile ? 'w-[260px]' : 'w-[280px] md:w-[300px]'
            }`}
          >
            <ProductCard producto={producto} />
          </div>
        ))}
      </div>

      {/* Scroll Indicators - Móvil */}
      {isMobile && (
        <div className="flex justify-center gap-1.5 mt-3">
          {productos.map((_, index) => (
            <div
              key={index}
              className="w-1.5 h-1.5 rounded-full bg-[#e91e8c]/30"
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Tratamientos({ onNavigate, initialTratamiento }: TratamientosProps) {
  const productos = useMemo(() => productosData as Producto[], []);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const sectionRef = useRef<HTMLDivElement>(null);
  const windowWidth = useWindowWidth();
  
  // Animaciones al hacer scroll
  useEffect(() => {
    const isMobile = windowWidth < 768;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { 
        threshold: isMobile ? 0 : 0.1, 
        rootMargin: isMobile ? '0px 0px 400px 0px' : '0px 0px -50px 0px' 
      }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [windowWidth]);

  const isVisible = (id: string) => visibleSections.has(id);
  
  const tratamientosConProductos = useMemo(() => {
    const tratamientosMap: Record<string, Producto[]> = {};
    
    productos.forEach((producto) => {
      producto.tratamientos.forEach((tratamiento) => {
        if (tratamiento.trim()) {
          if (!tratamientosMap[tratamiento]) {
            tratamientosMap[tratamiento] = [];
          }
          if (!tratamientosMap[tratamiento].find(p => p.codigo === producto.codigo)) {
            tratamientosMap[tratamiento].push(producto);
          }
        }
      });
    });

    return Object.entries(tratamientosMap)
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [productos]);

  useEffect(() => {
    if (initialTratamiento && tratamientosConProductos.length > 0) {
      const index = tratamientosConProductos.findIndex(([nombre]) => nombre === initialTratamiento);
      
      if (index !== -1) {
        const elementId = `tratamiento-${index}`;
        setTimeout(() => {
          const element = document.getElementById(elementId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Add a highlight effect
            element.classList.add('animate-highlight');
            setTimeout(() => element.classList.remove('animate-highlight'), 2000);
          }
        }, 100); // Short delay to ensure DOM is ready
      }
    }
  }, [initialTratamiento, tratamientosConProductos]);

  const handleProductClick = (producto: Producto) => {
    onNavigate('catalogo', { linea: producto.linea, producto });
  };

  return (
    <div ref={sectionRef} className="min-h-screen bg-[#f8f9f5] pt-24">
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-[#8abd31] via-[#8abd31] to-[#e91e8c] relative overflow-hidden">
        {/* Elementos decorativos */}
        <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute bottom-10 right-10 w-48 h-48 bg-[#e91e8c]/20 rounded-full blur-3xl" />
        
        <div className="section-padding relative z-10">
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              {/* <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6">
                <Sparkles className="w-4 h-4 text-white" />
                <span className="text-sm font-medium text-white">Soluciones Naturales</span>
              </div> */}
              <h1 className="font-serif text-4xl md:text-6xl text-white mb-6">
                Trata<span className="text-[#ffe4f0]">mientos</span>
              </h1>
              <p className="text-white/90 text-lg max-w-2xl mx-auto">
                Soluciones naturales respaldadas por la ciencia. Cada tratamiento 
                combina productos específicos para abordar tus necesidades de salud.
              </p>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-8 mt-12">
              <div className="text-center">
                <div className="text-4xl font-serif font-bold text-white">+90</div>
                <div className="text-white/70 text-sm">Tratamientos</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-serif font-bold text-white">+90</div>
                <div className="text-white/70 text-sm">Productos</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-serif font-bold text-white">4</div>
                <div className="text-white/70 text-sm">Líneas</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Treatments List */}
      <section className="py-16">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto space-y-16">
            {tratamientosConProductos.map(([nombre, productos], index) => {
              const info = tratamientosInfo[nombre] || tratamientosInfo.default;
              const Icono = info.icono;
              
              return (
                <div 
                  key={nombre}
                  id={`tratamiento-${index}`}
                  className={`reveal relative transition-all duration-700 scroll-mt-[215px] ${
                    isVisible(`tratamiento-${index}`) 
                      ? 'opacity-100 translate-y-0' 
                      : 'opacity-0 translate-y-2 md:translate-y-10'
                  }`}
                  style={{ transitionDelay: (windowWidth < 768) ? '0ms' : `${(index % 3) * 100}ms` }}
                >
                  {/* Treatment Header */}
                  <div className="flex items-center gap-4 md:gap-6 mb-6">
                    <div 
                      className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${info.color}20` }}
                    >
                      <Icono className="w-6 h-6 md:w-7 md:h-7" style={{ color: info.color }} />
                    </div>
                    <div className="flex-1">
                      <h2 className="font-serif text-xl md:text-2xl lg:text-3xl text-[#8abd31]">
                        {nombre}
                      </h2>
                      <p className="text-gray-500 text-xs md:text-sm">
                        {info.descripcion} • {productos.length} productos recomendados
                      </p>
                    </div>
                  </div>

                  {/* Products Display - Grid en desktop, Carrusel en móvil */}
                  <ProductosDisplay 
                    productos={productos}
                    onProductClick={handleProductClick}
                    windowWidth={windowWidth}
                  />

                  {/* Divider */}
                  {index < tratamientosConProductos.length - 1 && (
                    <div className="mt-12 md:mt-16 border-b border-gray-200" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#e91e8c]/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="section-padding relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* <div className="inline-flex items-center px-4 py-2 bg-[#e91e8c]/10 rounded-full mb-6">
              <span className="text-sm font-medium text-[#e91e8c]">¿Necesitas ayuda?</span>
            </div> */}
            <h2 className="font-serif text-4xl md:text-5xl text-[#8abd31] mb-6">
              ¿No encuentras tu <span className="text-[#e91e8c]">tratamiento</span>?
            </h2>
            <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
              Consulta nuestro catálogo completo o habla con un embajador para recibir 
              una recomendación personalizada.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => onNavigate('catalogo')}
                className="btn-accent flex items-center gap-2 shadow-lg shadow-[#e91e8c]/30"
              >
                <span>Ver catálogo completo</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => onNavigate('embajador')}
                className="px-6 py-3 border-2 border-[#e91e8c] text-[#e91e8c] rounded-full font-medium transition-all duration-300 hover:bg-[#e91e8c] hover:text-white"
              >
                Hablar con un embajador
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
